import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Avatar,
  Typography,
  Box,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Button
} from '@mui/material';
import { 
  MdClose, 
  MdEmail, 
  MdPhone, 
  MdBusiness, 
  MdLocationOn, 
  MdEvent, 
  MdUpdate,
  MdErrorOutline
} from 'react-icons/md';
import { getUserDetailsApi } from '../../services/UserService';

// API call
const fetchUserDetails = async () => {
  try {
    const response = await getUserDetailsApi();
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch user details');
  }
};

const ProfileDialog = ({ open, onClose }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      if (open) {
        setLoading(true);
        setError(null);
        try {
          const data = await fetchUserDetails();
          setUserData(data);
        } catch (err) {
          setError(err.message);
          console.error('Error fetching user details:', err);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [open, retryCount]);

  const handleClose = () => {
    setUserData(null);
    setError(null);
    onClose();
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setError(null);
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: "#ffffffff",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
          overflow: "hidden",
          maxWidth: "480px"
        }
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          p: 2, 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "space-between",
          borderBottom: "1px solid #f0f0f0",
          bgcolor: "#fafafa"
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
          Profile
        </Typography>
        <IconButton 
          onClick={handleClose} 
          size="small"
          sx={{ 
            color: "text.secondary",
            '&:hover': { bgcolor: "rgba(0,0,0,0.05)" }
          }}
        >
          <MdClose />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <CircularProgress size={36} />
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1.5 }}>
              Loading profile...
            </Typography>
          </Box>
        ) : error ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={4} px={3} textAlign="center">
            <MdErrorOutline size={36} color="#d32f2f" />
            <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0, fontWeight: 500 }}>
              Unable to load profile
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0, fontSize: '0.875rem' }}>
              {error}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleRetry}
              sx={{ borderRadius: 2 }}
            >
              Try Again
            </Button>
          </Box>
        ) : userData ? (
          <>
            {/* Avatar Section */}
            <Box display="flex" flexDirection="column" alignItems="center" pt={3} pb={2}>
              <Avatar
                sx={{
                  width: 72,
                  height: 72,
                  bgcolor: "error.main",
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  mb: 0,
                  boxShadow: 3
                }}
              >
                {userData.data.name?.charAt(0)?.toUpperCase() || "U"}
              </Avatar>
              
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
                {userData.data.name}
              </Typography>
              
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <Chip
                  label={userData.data.is_active ? "Active" : "Inactive"}
                  color={userData.data.is_active ? "success" : "error"}
                  size="small"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
                {userData.data.is_superuser && (
                  <Chip 
                    label="Admin" 
                    color="primary" 
                    size="small" 
                    sx={{ height: 20, fontSize: '0.7rem' }} 
                  />
                )}
              </Box>
            </Box>

            <Divider />

            {/* Details List */}
            <List sx={{ p: 0 }}>
              <ListItem sx={{ px: 2, py: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 36, color: "error.main" }}>
                  <MdEmail size={18} />
                </ListItemIcon>
                <ListItemText 
                  primary="Email" 
                  secondary={userData.data.email}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItem>

              <ListItem sx={{ px: 2, py: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 36, color: "error.main" }}>
                  <MdPhone size={18} />
                </ListItemIcon>
                <ListItemText 
                  primary="Mobile" 
                  secondary={userData.data.mobile_number || 'Not provided'}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItem>

              <ListItem sx={{ px: 2, py: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 36, color: "error.main" }}>
                  <MdBusiness size={18} />
                </ListItemIcon>
                <ListItemText 
                  primary="Service Center" 
                  secondary={userData.data.service_center_details?.name || 'Not assigned'}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItem>

              {userData.data.service_center_details?.address && (
                <ListItem sx={{ px: 2, py: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 36, color: "error.main" }}>
                    <MdLocationOn size={18} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Address" 
                    secondary={userData.data.service_center_details.address}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ 
                      variant: 'body2', 
                      color: 'text.secondary',
                      sx: { wordBreak: 'break-word' }
                    }}
                  />
                </ListItem>
              )}
            </List>

            <Divider />

            {/* Footer Info */}
            <Box display="flex" justifyContent="space-between" p={2} bgcolor="#fafafa">
              <Box display="flex" alignItems="center" gap={1}>
                <MdEvent size={16} color="#666" />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Created
                  </Typography>
                  <Typography variant="body2" fontSize="0.8rem">
                    {new Date(userData.data.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <MdUpdate size={16} color="#666" />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Last Login
                  </Typography>
                  <Typography variant="body2" fontSize="0.8rem">
                    {userData.data.last_login 
                      ? new Date(userData.data.last_login).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })
                      : "Never"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default ProfileDialog;